<template>
  <AIBasePanel
    :title="getLocaleText('ai_summary_assistant_panel.title')"
    @close="reset('close')"
    @home="reset('home')"
  >
    <div class="tw:grid tw:gap-4">
      <AIAssistantBaseChatRequest data-testid="chat-request">
        {{ getLocaleText("ai_summary_assistant_options.summarise") }}
      </AIAssistantBaseChatRequest>

      <AIAssistantBaseChatResponse
        data-testid="chat-response"
        :loading="streamSelectionLoading"
      >
        <div class="tw:grid tw:gap-2 tw:leading-tight">
          <AIAssistantBaseChatType
            :delay="500"
            :disabled="chatDisabled"
            @complete="updateChatState"
          >
            <template v-if="chatState === 'moment'">
              {{ getLocaleText("ai_summary_assistant_panel.just_a_moment") }}
            </template>
            <template v-else-if="chatState === 'select'">
              {{
                getLocaleText(
                  "ai_summary_assistant_panel.select_stream_message"
                )
              }}
            </template>
          </AIAssistantBaseChatType>

          <AIAssistantBaseChatTransition
            v-if="showStreamPicker"
            :appear="!chatDisabled"
          >
            <StreamPicker
              v-model="streamId"
              :disabled="aiStreamSummary.loading"
              :popper-width="null"
              single
              @update:model-value="selectStream"
            />
          </AIAssistantBaseChatTransition>
        </div>
      </AIAssistantBaseChatResponse>

      <AISummaryStreamResponse
        v-if="streamId"
        assistant
        data-testid="stream-response"
        :load="!aiStreamSummary.result"
        :stream-id="streamId"
      />
    </div>

    <template
      v-if="aiStreamSummary.result"
      #footer
    >
      <AISummaryStreamActions
        show-reload
        :stream-id="streamId"
      />
    </template>
  </AIBasePanel>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, onBeforeMount, ref } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import AIAssistantBaseChatRequest from "shared/components/AI/AIAssistant/AIAssistantBaseChatRequest.vue";
import AIAssistantBaseChatResponse from "shared/components/AI/AIAssistant/AIAssistantBaseChatResponse.vue";
import AIAssistantBaseChatTransition from "shared/components/AI/AIAssistant/AIAssistantBaseChatTransition.vue";
import AIAssistantBaseChatType from "shared/components/AI/AIAssistant/AIAssistantBaseChatType.vue";
import AIBasePanel from "shared/components/AI/AIBasePanel.vue";
import AISummaryStreamActions from "shared/components/AI/AISummary/AISummaryStreamActions.vue";
import AISummaryStreamResponse from "shared/components/AI/AISummary/AISummaryStreamResponse.vue";
import StreamPicker from "shared/components/streams/pickers/StreamPicker.vue";
import useAIStore, { type AISummary } from "shared/stores/ai";

const emit = defineEmits(["close", "home"]);

const aiStore = useAIStore();
const { aiSummaryStreamId, aiSummarySelectedTopic } = storeToRefs(aiStore);
const { getAISummary } = aiStore;

const streamSelectionLoading = ref(false);
const showStreamPicker = ref(false);
const chatState = ref<string>("moment");
const chatDisabled = ref(false);

const streamId = computed({
  get() {
    return aiSummaryStreamId.value || 0;
  },
  set(value) {
    aiSummaryStreamId.value = value;
  },
});

const aiStreamSummary = computed<Required<AISummary>>(() =>
  getAISummary(streamId.value)
);

function reset(event: "close" | "home") {
  aiSummaryStreamId.value = null;
  aiSummarySelectedTopic.value = null;
  emit(event);
}

function updateChatState() {
  if (chatState.value === "select") {
    showStreamPicker.value = true;
    streamSelectionLoading.value = false;
  } else {
    chatState.value = "select";
  }
}

function selectStream(id: number) {
  streamId.value = id;
}

onBeforeMount(() => {
  if (aiSummaryStreamId.value && aiSummarySelectedTopic.value?.assistant) {
    chatDisabled.value = true;
    chatState.value = "select";
    showStreamPicker.value = true;

    return;
  }

  streamSelectionLoading.value = true;
});
</script>

<style lang="scss">
@import "shared/css/components/ai.scss";
</style>
